import {
  getLastAuthError,
  resetAuthLoopGuard,
  StoredAuthError,
} from '@services/authLoopGuard'
import { logout, signinRedirect } from '@services/oauthService'
import { Button, Result, Typography } from 'antd'

const { Paragraph, Text } = Typography

function ClockSkewDetails({ error }: { error: StoredAuthError }) {
  const skew = error.skewSeconds ?? 0
  const minutes = Math.max(1, Math.round(Math.abs(skew) / 60))
  const direction = skew > 0 ? 'ahead of' : 'behind'
  const deviceTime = new Date()
  const estimatedServerTime = new Date(deviceTime.getTime() - skew * 1000)

  return (
    <div className="max-w-xl mx-auto text-left">
      <Paragraph>
        Your device clock appears to be about{' '}
        <Text strong>
          {minutes} minute{minutes !== 1 ? 's' : ''} {direction}
        </Text>{' '}
        the server, so sign-in tokens are rejected as soon as they are issued.
      </Paragraph>
      <Paragraph>
        <Text type="secondary">Device time:</Text>{' '}
        <Text code>{deviceTime.toLocaleString()}</Text>
        <br />
        <Text type="secondary">Estimated server time:</Text>{' '}
        <Text code>{estimatedServerTime.toLocaleString()}</Text>
      </Paragraph>
      <Paragraph>
        Please turn on <Text strong>&quot;Set time automatically&quot;</Text> in
        your computer&apos;s Date &amp; Time settings (or correct the clock
        manually), then click Try Again.
      </Paragraph>
    </div>
  )
}

function GenericDetails({ error }: { error: StoredAuthError | null }) {
  return (
    <div className="max-w-xl mx-auto text-left">
      <Paragraph>
        Sign-in failed repeatedly, so automatic retries were stopped to prevent
        an endless redirect loop.
      </Paragraph>
      {error?.message && (
        <Paragraph>
          <Text type="secondary">Technical details:</Text>
          <br />
          <Text code>{error.message}</Text>
        </Paragraph>
      )}
      <Paragraph>
        Please check that your computer&apos;s date &amp; time are correct and
        that your browser allows this site to store data (no blocking extensions
        or privacy settings), then click Try Again.
      </Paragraph>
    </div>
  )
}

const AuthErrorScreen = () => {
  const error = getLastAuthError()
  const isClockSkew = error?.type === 'clock-skew'

  const handleRetry = () => {
    resetAuthLoopGuard()
    signinRedirect({ redirectUri: window.location.pathname })
  }

  return (
    <Result
      status="warning"
      title={
        isClockSkew
          ? 'Incorrect computer date & time'
          : 'Unable to complete sign-in'
      }
      subTitle={
        isClockSkew ? (
          <ClockSkewDetails error={error} />
        ) : (
          <GenericDetails error={error} />
        )
      }
      extra={[
        <Button key="retry" type="primary" onClick={handleRetry}>
          Try Again
        </Button>,
        <Button key="signout" onClick={() => logout()}>
          Sign Out
        </Button>,
      ]}
    />
  )
}

export default AuthErrorScreen
